/**
 * Natural-sounding browser speech for face capture guidance.
 */

const FEMALE_VOICE_HINTS = [
  'aria', 'jenny', 'samantha', 'zira', 'victoria', 'karen', 'susan', 'hazel',
  'moira', 'fiona', 'tessa', 'neural', 'natural', 'google uk english female',
];

const MALE_VOICE_HINTS = ['guy', 'david', 'mark', 'daniel'];

function pickVariation(lines) {
  if (!lines) return '';
  const options = Array.isArray(lines) ? lines : [lines];
  if (!options.length) return '';
  return options[Math.floor(Math.random() * options.length)];
}

export function pickNaturalVoice(voices, { preferFemale = true } = {}) {
  if (!voices?.length) return null;
  const enVoices = voices.filter((v) => v.lang?.startsWith('en'));
  const pool = enVoices.length ? enVoices : voices;

  const neural = pool.find((v) => /neural|natural|online/i.test(v.name));
  if (neural) return neural;

  const hints = preferFemale ? FEMALE_VOICE_HINTS : MALE_VOICE_HINTS;
  const hinted = pool.find((v) => hints.some((hint) => v.name.toLowerCase().includes(hint)));
  if (hinted) return hinted;

  const local = pool.find((v) => v.localService);
  return local || pool[0];
}

export function speakNaturally(text, opts = {}) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  const spoken = String(text || '').trim();
  if (!spoken) return;

  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return;

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(spoken);
  utterance.rate = opts.rate ?? 0.9;
  utterance.pitch = opts.pitch ?? 1.04;
  utterance.volume = opts.volume ?? 1;
  utterance.lang = opts.lang ?? 'en-IN';

  const voice = pickNaturalVoice(voices, opts);
  if (voice) utterance.voice = voice;
  window.speechSynthesis.speak(utterance);
}

export const STEP_VOICE = {
  front: [
    'Look straight at the camera for me, and keep your chin level.',
    'Face the camera directly — nice and relaxed.',
  ],
  left: [
    'Now turn your head gently to the right. Take your time.',
    'Slowly rotate to your right profile — keep your shoulders still.',
  ],
  right: [
    'Turn your head gently to the left now. Easy does it.',
    'Rotate slowly to your left profile — keep your head level.',
  ],
  aligned: [
    'Perfect — hold still for just a moment.',
    'That looks great. Stay right there.',
  ],
  captured: [
    'Got it. Nice work.',
    'Captured. Looking good.',
  ],
};

export const GUIDANCE_VOICE = {
  'Position your face': [
    'Centre your face in the frame for me.',
    'Bring your face into the middle of the guide.',
  ],
  'Move face left': [
    'Shift a little to your left.',
    'Try moving slightly left.',
  ],
  'Move face right': [
    'Shift a little to your right.',
    'Try moving slightly right.',
  ],
  'Move closer': [
    'Come a bit closer to the camera.',
    'Move in slightly — a little closer.',
  ],
  'Move a little farther': [
    'Step back just a touch.',
    'Move a little farther from the camera.',
  ],
  'Tilt head right': [
    'Level your head — tilt slightly to the right.',
    'Straighten up — a small tilt to the right.',
  ],
  'Tilt head left': [
    'Level your head — tilt slightly to the left.',
    'Straighten up — a small tilt to the left.',
  ],
  'Lower your head': [
    'Lower your chin just a little.',
    'Tilt your head down slightly.',
  ],
  'Raise your head': [
    'Lift your chin a touch.',
    'Raise your head slightly.',
  ],
  'Perfect alignment!': STEP_VOICE.aligned,
  'Perfect! Hold still.': STEP_VOICE.aligned,
  'Turn your head to the right': STEP_VOICE.left,
  'Turn your head to the left': STEP_VOICE.right,
  'Keep head level': [
    'Keep your head level — avoid tilting.',
    'Try to keep your head straight.',
  ],
  'Adjust your position': [
    'Adjust your position until you fit the guide.',
    'Small adjustments — centre yourself in the frame.',
  ],
  'Hold still': [
    'Almost there — hold still.',
    'Keep steady for a moment.',
  ],
};

export function guidanceToSpeech(message) {
  const key = String(message || '').trim();
  if (!key) return '';
  return pickVariation(GUIDANCE_VOICE[key] || key);
}

export function stepToSpeech(step) {
  return pickVariation(STEP_VOICE[step] || '');
}

export function lightingToSpeech(message) {
  const msg = String(message || '').trim();
  if (msg) return msg.replace(/\.$/, '') + '.';
  return 'The lighting is a bit low — try facing a brighter spot with even light.';
}
